import { Alert, AlertIcon, Box, Button, Flex, Heading, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  EventRevisionState,
  SourcingEventStatus,
  type SourcingEventV2Dto,
  useCreateEventChangeMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { EventChangeEditor } from "./EventChangeEditor";
import { ChangeLogList } from "./ChangeLogList";

interface Props {
  event: SourcingEventV2Dto;
}

export const ChangeNoticeSection = ({ event }: Props) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const editor = useDisclosure();
  const [create, { isLoading: isCreating }] = useCreateEventChangeMutation();

  const isPrivate = event.eventContext.type === "Private";
  const changes = event.changes ?? [];
  const newestFirst = [...changes].reverse();
  const draft = newestFirst.find((c) => c.state === EventRevisionState.Draft);
  const pending = newestFirst.find((c) => c.state === EventRevisionState.PublicationPending);
  const published = changes.filter((c) => c.state === EventRevisionState.Published);

  const deadline = event.eventData.bidSubmissionDeadline;
  const canStartChange =
    event.status === SourcingEventStatus.Published &&
    !draft &&
    !pending &&
    deadline != null &&
    new Date(deadline).getTime() > Date.now();

  if (!canStartChange && !draft && !pending && published.length === 0) return null;

  const handleStart = async () => {
    try {
      await create({ eventId: event.id }).unwrap();
      editor.onOpen();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="white" p="5">
      <Flex align="center" justify="space-between" mb={published.length > 0 || draft || pending ? "4" : "0"}>
        <Heading as="h3" size="sm">
          {isPrivate ? t("Changes to the request") : t("Changes to the announcement")}
        </Heading>
        {canStartChange && (
          <Button size="sm" colorScheme="teal" variant="outline" onClick={handleStart} isLoading={isCreating}>
            {isPrivate ? t("Change the request") : t("Change the announcement")}
          </Button>
        )}
        {draft && (
          <Button size="sm" colorScheme="teal" onClick={editor.onOpen}>
            {t("Continue the change")}
          </Button>
        )}
      </Flex>

      <Stack spacing="4">
        {pending && (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <Box>
              <Text>{t("A change notice has been submitted and is awaiting confirmation from Doffin.")}</Text>
              <Text color="smMuted">{pending.changeDescription}</Text>
            </Box>
          </Alert>
        )}

        {draft && editor.isOpen && (
          <EventChangeEditor
            key={draft.id}
            event={event}
            draft={draft}
            isOpen={editor.isOpen}
            onClose={editor.onClose}
          />
        )}

        {published.length > 0 && <ChangeLogList revisions={published} />}
      </Stack>
    </Box>
  );
};
